import { Button } from '@/components/ui/Button'
import { Modal } from '@/components/ui/Modal'
import type { AutoApplyFilters, JobFilterChoices, JobMatch } from '@/types/api'
import { filterChips, formatNumber } from './filters'

export const COUNT_CHOICES = [25, 50, 100, 200] as const

/** How many "apply with these filters" sends: the choice, capped by what is ready and what the plan has left. */
export function effectiveCount(choice: number, ready: number, remaining: number | null): number {
  return Math.max(0, Math.min(choice, ready, remaining ?? Number.POSITIVE_INFINITY))
}

/**
 * The sticky footer: "Apply to 50 jobs with these filters" (with the count
 * dropdown), or "Apply to 12 selected" once rows are ticked.
 */
export function ApplyBar({
  ready,
  remaining,
  choice,
  onChoice,
  selectedCount,
  onClearSelection,
  onApplyFiltered,
  onApplySelected,
  busy,
}: {
  ready: number
  remaining: number | null
  choice: number
  onChoice: (next: number) => void
  selectedCount: number
  onClearSelection: () => void
  onApplyFiltered: (count: number) => void
  onApplySelected: () => void
  busy: boolean
}) {
  const count = effectiveCount(choice, ready, remaining)
  const outOfApplications = remaining === 0

  return (
    <div className="action-bar mt-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
        <p className="min-w-0 text-[12.5px] leading-snug text-ink-muted">
          {selectedCount > 0 ? (
            <>
              <span className="num font-medium text-ink">{selectedCount}</span> selected
              <span aria-hidden="true"> · </span>
              <button type="button" onClick={onClearSelection} className="font-medium text-ink-soft underline-offset-2 hover:text-ink hover:underline">
                Clear
              </button>
            </>
          ) : (
            <>
              Best matches first
              {remaining != null && (
                <>
                  <span aria-hidden="true"> · </span>
                  <span className="num">{formatNumber(remaining)}</span> left this month
                </>
              )}
            </>
          )}
        </p>
        {/* On phones the assistant's floating Ask button sits at the right of this row: leave it room. */}
        <div className="flex items-center gap-2 pr-[96px] sm:ml-auto md:pr-0">
          {selectedCount > 0 ? (
            <Button className="h-11 flex-1 md:h-10 md:flex-none md:px-6" onClick={onApplySelected} loading={busy}>
              <span>
                Apply to <span className="num">{selectedCount}</span> selected
              </span>
            </Button>
          ) : (
            <>
              {ready > COUNT_CHOICES[0] && !outOfApplications && (
                <label className="relative shrink-0">
                  <span className="sr-only">How many jobs to apply to</span>
                  <select
                    value={choice}
                    onChange={(event) => onChoice(Number(event.target.value))}
                    className="field-box num h-11 w-[84px] py-0 pl-3 pr-7 text-[14px] md:h-10"
                  >
                    {COUNT_CHOICES.map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <Button
                className="h-11 min-w-0 flex-1 md:h-10 md:flex-none md:px-6"
                onClick={() => onApplyFiltered(count)}
                loading={busy}
              >
                {outOfApplications ? (
                  'Get more applications'
                ) : (
                  <span className="truncate">
                    Apply to <span className="num">{formatNumber(count)}</span> job{count === 1 ? '' : 's'}
                    <span className="hidden xs:inline"> with these filters</span>
                  </span>
                )}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export type ApplyIntent = { kind: 'filters'; count: number } | { kind: 'selected'; jobs: JobMatch[] }

/** What will happen, before it happens: the count, the filters (or picks), and the plan left. */
export function ConfirmApplyDialog({
  intent,
  filters,
  choices,
  sourceLabels,
  defaultScore,
  remaining,
  remainingToday,
  busy,
  onConfirm,
  onClose,
}: {
  intent: ApplyIntent
  filters: AutoApplyFilters
  choices?: JobFilterChoices
  sourceLabels?: Record<string, string>
  defaultScore: number
  remaining: number | null
  remainingToday: number | null
  busy: boolean
  onConfirm: () => void
  onClose: () => void
}) {
  const count = intent.kind === 'filters' ? intent.count : intent.jobs.length
  const chips = filterChips(filters, { choices, sourceLabels, defaultScore })
  const overToday = remainingToday != null && count > remainingToday
  const jobsWord = `job${count === 1 ? '' : 's'}`

  return (
    <Modal
      open
      onClose={onClose}
      size="sm"
      title={intent.kind === 'filters' ? `Apply to ${formatNumber(count)} ${jobsWord}?` : `Apply to ${count} selected ${jobsWord}?`}
      description="We send them best match first. Anything we cannot answer for you lands in Needs you instead of being guessed."
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={onConfirm} loading={busy}>
            <span>
              Apply to <span className="num">{formatNumber(count)}</span> {jobsWord}
            </span>
          </Button>
        </>
      }
    >
      <div className="space-y-4 pb-3">
        {intent.kind === 'filters' ? (
          <div>
            <p className="label-eyebrow mb-1.5">Filters</p>
            {chips.length ? (
              <div className="flex flex-wrap gap-1.5">
                {chips.map((chip) => (
                  <span key={chip.id} className="inline-flex h-7 max-w-full items-center truncate rounded-pill bg-surface-muted px-2.5 text-[12.5px] text-ink">
                    {chip.label}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-[13.5px] text-ink-soft">
                None. Your best matches at <span className="num">{filters.minMatchScore ?? defaultScore}%</span> or higher.
              </p>
            )}
          </div>
        ) : (
          <div>
            <p className="label-eyebrow mb-1.5">Your picks</p>
            <ul className="space-y-1 text-[13.5px] text-ink-soft">
              {intent.jobs.slice(0, 4).map((match) => (
                <li key={match.id} className="truncate">
                  <span className="font-medium text-ink">{match.job.title}</span> · {match.job.companyName}
                </li>
              ))}
              {intent.jobs.length > 4 && (
                <li className="text-ink-muted">
                  and <span className="num">{intent.jobs.length - 4}</span> more
                </li>
              )}
            </ul>
          </div>
        )}

        <div className="rounded-field bg-surface-muted px-3 py-2.5 text-[13px] text-ink-soft">
          {remaining != null ? (
            <>
              Your plan has <span className="num font-medium text-ink">{formatNumber(remaining)}</span> applications left this month
              {remainingToday != null && (
                <>
                  {' '}
                  (<span className="num">{formatNumber(remainingToday)}</span> today)
                </>
              )}
              . This uses <span className="num font-medium text-ink">{formatNumber(count)}</span>.
            </>
          ) : (
            <>
              This uses <span className="num font-medium text-ink">{formatNumber(count)}</span> of your plan&apos;s applications.
            </>
          )}
          {overToday && (
            <p className="mt-1.5 text-warning">
              Only <span className="num">{formatNumber(remainingToday!)}</span> can go out today. The rest are skipped for now.
            </p>
          )}
        </div>
      </div>
    </Modal>
  )
}
